import { skipToken } from '@reduxjs/toolkit/dist/query';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import type { SubscriptionBillingCycle } from '@bugbug/core/types/subscriptions';
import ServerErrorInfo from '~/components/ServerErrorInfo';
import { RetryButton } from '~/components/ServerErrorInfo/ServerErrorInfo.styled';
import useQueryString from '~/hooks/useQueryString';
import { selectSubscription } from '~/modules/organization/organization.selectors';
import { useAppSelector } from '~/modules/store';
import { useGetSubscriptionPricePreviewQuery } from '~/modules/subscriptions/subscriptions.api';

import {
  SUBSCRIPTION_DETAILS,
  SUBSCRIPTION_PERIOD_DETAILS,
} from '../Subscription/Subscription.constants';
import { isHigherPlan, getParallelsLabel } from '../Subscription/Subscription.helpers';
import { useSelectedSubscriptionProducts } from '../Subscription/Subscription.hooks';

import { useCheckoutWizardContext } from './CheckoutWizard.context';
import * as S from './OrderSummary.styled';

import OrderSummaryIcon from '~/images/order-summary-icon.svg?react';

export const OrderSummary = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'orderSummary' });
  const activeSubscription = useAppSelector(selectSubscription);
  const { countryCode } = useCheckoutWizardContext();

  const { billingCycle, coupon } = useQueryString<{
    billingCycle: SubscriptionBillingCycle;
    coupon?: string;
  }>();

  const {
    data: products,
    isError: isProductsError,
    refetch: refetchProducts,
    isFetching: isFetchingProducts,
  } = useSelectedSubscriptionProducts();

  const {
    data: pricePreview,
    isFetching: isFetchingPricePreview,
    isError: isPricePreviewError,
    refetch: refetchPricePreview,
  } = useGetSubscriptionPricePreviewQuery(
    products
      ? {
          price: products.plan.priceNickname,
          addons: products?.parallel
            ? [{ price: products.parallel.priceNickname, quantity: products.parallel.value }]
            : [],
          coupon,
          countryCode,
        }
      : skipToken,
  );

  if (isProductsError && !isFetchingProducts) {
    return (
      <S.Container>
        <ServerErrorInfo
          isVisible
          onRetry={refetchProducts}
          errorText={t('error.products', 'An error occurred while loading order summary.')}
        />
      </S.Container>
    );
  }

  if (!products) return null;

  const isPlanUpgrade =
    !activeSubscription ||
    activeSubscription.isTrial ||
    isHigherPlan(products.plan, activeSubscription);

  const hasPaidPlan =
    activeSubscription && activeSubscription.type !== 'free' && !activeSubscription.isTrial;

  return (
    <S.Container aria-label="Order summary">
      <S.Row>
        <S.Title>{t('title', 'Order summary')}</S.Title>
        <OrderSummaryIcon width={24} height={24} />
      </S.Row>
      <S.Section>
        {isPlanUpgrade ? (
          <>
            <S.Row>
              <S.RowTitle>{t('plan.label', 'Plan')}</S.RowTitle>
              <S.RowValue>{SUBSCRIPTION_DETAILS[products.plan.type].name.toUpperCase()}</S.RowValue>
            </S.Row>
            <S.Row>
              <S.RowTitle>{t('parallelRuns.label', 'Parallel runs')}</S.RowTitle>
              <S.RowValue>{getParallelsLabel(products.parallel?.value ?? 0)}</S.RowValue>
            </S.Row>
            <S.Row>
              <S.RowTitle>{t('billingCycle.label', 'Billing cycle')}</S.RowTitle>
              <S.RowValue>{SUBSCRIPTION_PERIOD_DETAILS[billingCycle].name}</S.RowValue>
            </S.Row>
            {hasPaidPlan && (
              <S.Row>
                <S.RowTitle>{t('subscriptionStarts.label', 'Subscription starts')}</S.RowTitle>
                <S.RowValue>{t('subscriptionStarts.value', 'Today')}</S.RowValue>
              </S.Row>
            )}
          </>
        ) : (
          <S.Row>
            <S.RowTitle>{t('parallelUpgrade.label', 'Upgrade to')}</S.RowTitle>
            <S.RowValue>{getParallelsLabel(products.parallel?.value ?? 0)}</S.RowValue>
          </S.Row>
        )}
        {pricePreview && pricePreview.upgradeCost.gt(0) && hasPaidPlan && (
          <S.Row>
            <S.RowTitle>{t('upgradeCost.label', 'Upgrade cost')}</S.RowTitle>
            <S.RowValue>${pricePreview.upgradeCost.toFixed(2)}</S.RowValue>
          </S.Row>
        )}
        {pricePreview && pricePreview.discountUnusedTime.gt(0) && (
          <S.Row>
            <S.RowTitle>
              {t('discountUnusedTime.label', 'Discount for unused time on previous plan')}
            </S.RowTitle>
            <S.RowValue>-${pricePreview.discountUnusedTime.toFixed(2)}</S.RowValue>
          </S.Row>
        )}
        {pricePreview && !pricePreview.invalidCoupon && pricePreview.discountValue.gt(0) && (
          <S.Row>
            <S.PromoTitle>{t('promoCode.label', 'Promo code')}</S.PromoTitle>
            <S.PromoValue>{coupon}</S.PromoValue>
          </S.Row>
        )}
        {pricePreview?.invalidCoupon && (
          <S.Error>{t('error.invalidCoupon', 'Promo code is invalid or expired.')}</S.Error>
        )}
        {!isPlanUpgrade && activeSubscription?.end && (
          <S.PaymentInfo>
            {t(
              'paymentInfo',
              'You only pay for the remaining time until the end of current billing cycle ({{date}}).',
              { date: dayjs(activeSubscription.end).format('DD MMM YYYY') },
            )}
          </S.PaymentInfo>
        )}
        {pricePreview && pricePreview.totalBilledToday.lt(0) && (
          <S.PaymentInfo>
            {t('overpaymentInfo', 'The overpayment will be spend on future payments.')}
          </S.PaymentInfo>
        )}
      </S.Section>
      <S.Separator />
      {pricePreview && !isFetchingPricePreview && (
        <S.Section>
          <S.Row>
            <S.RowTitle>{t('subtotal.label', 'Subtotal')}</S.RowTitle>
            <S.RowValue>
              {!pricePreview.invalidCoupon && pricePreview.discountValue.gt(0) && (
                <S.DiscountedValue>
                  ${pricePreview.subtotal.plus(pricePreview.discountValue).toFixed(2)}
                </S.DiscountedValue>
              )}
              ${pricePreview.subtotal.toFixed(2)}
            </S.RowValue>
          </S.Row>
          <S.Row>
            <S.RowTitle>
              {t('vat.label', 'VAT ({{taxPercent}}%)', {
                taxPercent: pricePreview.taxPercent.toString(),
              })}
            </S.RowTitle>
            <S.RowValue>${pricePreview.vat.toFixed(2)}</S.RowValue>
          </S.Row>
          <S.Row>
            <S.TotalTitle>{t('total.label', 'Total billed today')}</S.TotalTitle>
            <S.TotalValue>${pricePreview.totalBilledToday.toFixed(2)}</S.TotalValue>
          </S.Row>
        </S.Section>
      )}
      {isFetchingPricePreview && <S.PriceLoader />}
      {!isFetchingPricePreview && isPricePreviewError && (
        <p>
          <S.Error>
            {t('error.pricePreview', 'An error occurred while loading price preview.')}
          </S.Error>
          <RetryButton type="button" onClick={refetchPricePreview}>
            {t('error.retry', 'Try again')}
          </RetryButton>
        </p>
      )}
    </S.Container>
  );
};
